import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import { subscribeMonthlyManualPayments } from "../services/manualPaymentService";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right.svg";
import "../../../../css/Finance/MonthlyPayments.css";
import "../../../../css/Marketing/WebsiteCharges.css";

const chargeTypes = [
  "Monthly Fees",
  "AML Checks",
  "Paraplanning",
  "Website & Graphics",
  "Suspense",
  "Miscellaneous",
];
const breakdownTabs = [
  { id: "monthly", label: "Monthly Charges" },
  { id: "advances", label: "Advances" },
  { id: "additional-payments", label: "Additional Payments" },
];

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value,
  );
const paymentAmount = (payment = {}) => {
  const amount = Number(payment.amountPaid || 0);
  return payment.paymentType === "additional_payment" ? Math.abs(amount) : amount;
};

const ChargesBreakdown = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    chargeRecords,
    chargesLoading,
    advisersByEmail,
  } = useAdviserCharges();
  const { advisers } = useAdviserList();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(() =>
    breakdownTabs.some((tab) => tab.id === searchParams.get("tab"))
      ? searchParams.get("tab")
      : "monthly",
  );
  const [payments, setPayments] = useState([]);
  const [paymentSort, setPaymentSort] = useState({
    key: "adviserName",
    direction: 1,
  });
  const [sort, setSort] = useState({ key: "adviser", direction: 1 });
  const requestedMonth = searchParams.get("month");
  const requestedTab = searchParams.get("tab");

  useEffect(() => {
    if (monthOptions.some((option) => option.value === requestedMonth)) {
      setSelectedMonth(requestedMonth);
    }
  }, [monthOptions, requestedMonth, setSelectedMonth]);
  useEffect(() => {
    if (breakdownTabs.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);
  useEffect(
    () => subscribeMonthlyManualPayments(setPayments, () => setPayments([])),
    [],
  );
  const paymentRows = useMemo(() => {
    const type = activeTab === "advances" ? "advance" : "additional_payment";
    return payments
      .filter(
        (payment) =>
          payment.paymentMonth === selectedMonth &&
          payment.paymentType === type,
      )
      .filter((payment) =>
        `${payment.adviserName} ${payment.brandName} ${payment.description}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
      .sort((left, right) => {
        const leftValue =
          paymentSort.key === "amountPaid"
            ? paymentAmount(left)
            : String(left[paymentSort.key] || "");
        const rightValue =
          paymentSort.key === "amountPaid"
            ? paymentAmount(right)
            : String(right[paymentSort.key] || "");
        const comparison =
          typeof leftValue === "number"
            ? leftValue - rightValue
            : leftValue.localeCompare(rightValue, "en-GB");
        return comparison * paymentSort.direction;
      });
  }, [activeTab, paymentSort, payments, search, selectedMonth]);
  const paymentTotal = useMemo(
    () =>
      paymentRows.reduce(
        (total, payment) => total + paymentAmount(payment),
        0,
      ),
    [paymentRows],
  );
  const changePaymentSort = (key) =>
    setPaymentSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));
  const rows = useMemo(() => {
    const byAdviser = new Map();
    const adviserListByEmail = new Map(
      advisers.map((adviser) => [
        String(adviser.email || adviser.id || "")
          .trim()
          .toLowerCase(),
        adviser,
      ]),
    );
    chargeRecords.forEach((record) => {
      if (record.excludeFromBalance === true) return;
      const email = String(record.adviserEmail || "")
        .trim()
        .toLowerCase();
      const adviserFromContext = adviserListByEmail.get(email);
      const adviserDetails = advisersByEmail[email]?.adviser;
      const adviser =
        adviserFromContext?.name ||
        adviserDetails?.adviser ||
        record.adviser ||
        email ||
        "Unassigned";
      const practice =
        adviserFromContext?.brandName ||
        adviserDetails?.brandName ||
        adviserDetails?.practice ||
        record.brandName ||
        record.practice ||
        "-";
      const key = email || adviser;
      const existing = byAdviser.get(key) || { adviser, practice, email, total: 0 };
      const type = chargeTypes.includes(record.chargeType)
        ? record.chargeType
        : "Miscellaneous";
      existing[type] =
        (existing[type] || 0) + Number(record.amountCharged || 0);
      existing.total += Number(record.amountCharged || 0);
      byAdviser.set(key, existing);
    });
    return [...byAdviser.values()]
      .filter((row) =>
        `${row.adviser} ${row.practice}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
      .sort(
        (a, b) =>
          (typeof a[sort.key] === "string"
            ? a[sort.key].localeCompare(b[sort.key])
            : a[sort.key] - b[sort.key]) * sort.direction,
      );
  }, [advisers, advisersByEmail, chargeRecords, search, sort]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => {
          chargeTypes.forEach((type) => {
            sum[type] = (sum[type] || 0) + (row[type] || 0);
          });
          sum.total += row.total;
          return sum;
        },
        { total: 0 },
      ),
    [rows],
  );
  const columns = [
    ["adviser", "Adviser"],
    ["practice", "Practice"],
    ...chargeTypes.map((type) => [type, type]),
    ["total", "Total Charged"],
  ];
  const changeSort = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Charges Breakdown</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container monthly-payments-page column gap-m">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            {
              label: "Monthly Payments",
              to: APP_ROUTES.CHARGES_SUMMARY_DASHBAORD,
            },
            { label: "Charges Breakdown" },
          ]}
        />
        <header className="space-between-row">
          <h3 className="FP_H3">Charges Breakdown</h3>
        </header>
        <Tabs
          tabs={breakdownTabs}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            navigate(
              `${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}&tab=${tab}`,
              { replace: true },
            );
          }}
        />
        <div className="space-between-row gap-m">
          <RadioGroup
            name="charges-breakdown-month"
            options={monthOptions}
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            inline
          />
          <SearchField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
          />
          {/* <div className="row gap-m">
            <PrimaryButton label="Add New Charge" />
          </div> */}
        </div>
        <section className="website-dashboard-card table-scroll-card charges-breakdown-card">
          {activeTab === "advances" ||
            activeTab === "additional-payments" ? (
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table
                className="website-table"
                aria-label={
                  activeTab === "advances" ? "Advances" : "Additional Payments"
                }
              >
                <thead>
                  <tr>
                    {[
                      ["adviserName", "Adviser"],
                      ["brandName", "Practice"],
                      ["description", "Description"],
                      [
                        "amountPaid",
                        activeTab === "advances"
                          ? "Advances"
                          : "Additional Payment",
                      ],
                    ].map(([key, label]) => (
                      <th key={key} className="FP_Help_Text_Semibold uppercase">
                        <button
                          type="button"
                          className="table-heading-content table-sort-button"
                          onClick={() => changePaymentSort(key)}
                        >
                          {label} <SortIcon aria-hidden="true" />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.length ? (
                    paymentRows.map((payment) => (
                      <tr key={payment.id}>
                        <td className="FP_Help_Text_Semibold black">
                          <button
                            type="button"
                            className="table-adviser-link"
                            onClick={() =>
                              navigate(
                                `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(payment.adviserEmail)}?month=${selectedMonth}&view=${activeTab}`,
                              )
                            }
                          >
                            {payment.adviserName}
                          </button>
                        </td>
                        <td className="FP_Help_Text black">
                          {payment.brandName || "-"}
                        </td>
                        <td className="FP_Help_Text black">
                          {payment.description || "-"}
                        </td>
                        <td className="FP_Help_Text black website-charge-group-divider">
                          <div className="row width-full justify-flex-end align-center gap-m">
                            <span>{money(paymentAmount(payment))}</span>
                            <button
                              type="button"
                              className="table-row-action"
                              aria-label={`View ${payment.adviserName} payments`}
                              onClick={() =>
                                navigate(
                                  `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(payment.adviserEmail)}?month=${selectedMonth}&view=${activeTab}`,
                                )
                              }
                            >
                              <ArrowRightIcon aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="FP_Content">
                        No{" "}
                        {activeTab === "advances"
                          ? "advances"
                          : "additional payments"}{" "}
                        for this month.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="website-charges-total-row">
                    <td className="FP_Help_Text black" colSpan="3">
                      {activeTab === "advances"
                        ? "Total Advances"
                        : "Total Additional Payments"}
                    </td>
                    <td className="FP_Help_Text black text-align-right charges-breakdown-total-cell">
                      <span>{money(paymentTotal)}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table
                className="website-table"
                aria-label="Monthly charges breakdown"
              >
                <thead>
                  <tr>
                    {columns.map(([key, label]) => (
                      <th key={key} className="FP_Help_Text_Semibold uppercase">
                        <button
                          type="button"
                          className="table-heading-content table-sort-button"
                          onClick={() => changeSort(key)}
                        >
                          {label} <SortIcon aria-hidden="true" />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chargesLoading ? (
                    <tr>
                      <td colSpan={columns.length} className="FP_Content">
                        Loading…
                      </td>
                    </tr>
                  ) : rows.length ? (
                    rows.map((row) => (
                      <tr key={row.adviser}>
                        <td className="FP_Help_Text_Semibold black">
                          <button
                            type="button"
                            className="table-adviser-link"
                            onClick={() =>
                              navigate(
                                `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(row.email || row.adviser)}?month=${selectedMonth}`,
                              )
                            }
                          >
                            {row.adviser}
                          </button>
                        </td>
                        <td className="FP_Help_Text black">{row.practice}</td>
                        {chargeTypes.map((type) => (
                          <td
                            key={type}
                            className="FP_Help_Text black text-align-right "
                          >
                            {row[type] ? money(row[type]) : "-"}
                          </td>
                        ))}
                        <td className="FP_Help_Text black website-charge-group-divider">
                          <div className="row width-full justify-flex-end align-center gap-m">
                            <span>{money(row.total)}</span>
                            <button
                              type="button"
                              className="table-row-action"
                              aria-label={`View ${row.adviser} charges`}
                              onClick={() =>
                                navigate(
                                  `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(row.email || row.adviser)}?month=${selectedMonth}`,
                                )
                              }
                            >
                              <ArrowRightIcon aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="FP_Content">
                        No charges for this month.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="website-charges-total-row">
                    <td className="FP_Help_Text black" colSpan="2">
                      Total Charged
                    </td>
                    {chargeTypes.map((type) => (
                      <td
                        key={type}
                        className="FP_Help_Text black text-align-right"
                      >
                        {money(totals[type] || 0)}
                      </td>
                    ))}
                    <td className="FP_Help_Text black text-align-right charges-breakdown-total-cell">
                      <span>{money(totals.total)}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default withMonthlyPaymentsAccess(ChargesBreakdown);
